<script setup>
import { computed } from 'vue'
import { useBreakpoint } from '@/composables/useBreakpoint'

const props = defineProps({
  modelValue: { type: [String, Number], required: true },
  options: { type: Array, required: true }, // [{ value, label, count?, tone? }]
  size: { type: String, default: 'md' }, // sm|md
})
const emit = defineEmits(['update:modelValue'])

const { isMobile } = useBreakpoint()

const toneClass = (tone) => ({
  ok:     'text-[color:var(--ok)]',
  warn:   'text-[color:var(--warn)]',
  danger: 'text-[color:var(--danger)]',
  info:   'text-[color:var(--info)]',
  muted:  'text-[color:var(--muted)]',
}[tone] || '')

const selectedOption = computed(() =>
  props.options.find((o) => o.value === props.modelValue) ?? null,
)
const selectedLabel = computed(() => selectedOption.value?.label ?? '—')
const selectedCount = computed(() => selectedOption.value?.count)

const onSelectChange = (e) => {
  const raw = e.target.value
  const match = props.options.find((o) => String(o.value) === raw)
  emit('update:modelValue', match ? match.value : raw)
}
</script>

<template>
  <!-- Mobile: native select styled as a pill to match dropdown UX across the app -->
  <label
    v-if="isMobile"
    class="ui-seg-select relative inline-flex items-center rounded-full bg-[color:var(--surfaceAlt)] border border-[color:var(--line)] max-w-full h-7 pl-3 pr-7 text-xs text-[color:var(--ink)]"
  >
    <span class="truncate">{{ selectedLabel }}</span>
    <span
      v-if="selectedCount != null"
      class="ml-1 tabular-nums text-[color:var(--muted)]"
    >{{ selectedCount }}</span>
    <svg
      class="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
      width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"
    >
      <path d="M1 3l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    <select
      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      :value="modelValue"
      @change="onSelectChange"
    >
      <option
        v-for="opt in options"
        :key="opt.value"
        :value="opt.value"
      >
        {{ opt.label }}{{ opt.count != null ? ` (${opt.count})` : '' }}
      </option>
    </select>
  </label>

  <!-- Desktop / tablet: segmented pill -->
  <div
    v-else
    role="tablist"
    class="ui-seg ui-segmented inline-flex items-center gap-0.5 p-0.5 rounded-full bg-[color:var(--surfaceAlt)] border border-[color:var(--line)] max-w-full overflow-x-auto"
    :class="size === 'sm' ? 'h-7' : 'h-7 md:h-9'"
  >
    <button
      v-for="opt in options"
      :key="opt.value"
      type="button"
      role="tab"
      :aria-selected="modelValue === opt.value"
      :class="[
        'inline-flex items-center gap-1.5 rounded-full transition-colors whitespace-nowrap shrink-0',
        size === 'sm' ? 'h-6 px-2 text-xs' : 'h-6 md:h-8 px-2 md:px-2.5 text-xs md:text-sm',
        modelValue === opt.value
          ? 'ui-seg-selected bg-[color:var(--surface)] text-[color:var(--ink)] shadow-[var(--shadow-sm)]'
          : 'text-[color:var(--inkSoft)] hover:text-[color:var(--ink)]',
      ]"
      @click="$emit('update:modelValue', opt.value)"
    >
      <span :class="opt.tone && modelValue !== opt.value ? toneClass(opt.tone) : ''">{{ opt.label }}</span>
      <span
        v-if="opt.count != null"
        class="tabular-nums text-[color:var(--muted)]"
      >{{ opt.count }}</span>
    </button>
  </div>
</template>

<style scoped>
.ui-segmented {
  scrollbar-width: none;
}
.ui-segmented::-webkit-scrollbar {
  display: none;
}
</style>
